import { useEffect, useCallback } from 'react';
import { watchNetworkStatus, checkWithRetry } from '@/utils/connectivity';
import { useAtom, useAtomValue } from 'jotai/react';
import { useInterval } from 'react-use';
import { ConnectivityStateAtom } from '@/stores/connectivity';

/**
 * 用于检测网络连接状态的自定义 Hook
 *
 * @param autoCheck 是否自动检测，默认为 true
 * @param checkInterval 自动检测间隔（毫秒），默认 30 秒
 * @returns 连接状态和控制函数
 */
export const useConnectivity = (
  autoCheck: boolean = true,
  checkInterval: number = 30000
) => {
  const [state, setState] = useAtom(ConnectivityStateAtom);

  // 检测 Google 是否可访问
  const checkGoogle = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      isGoogleAccessible: null,
      error: null,
    }));

    try {
      const isAccessible = await checkWithRetry();

      setState((prev) => ({
        ...prev,
        isGoogleAccessible: isAccessible,
        isLoading: false,
        lastChecked: new Date(),
      }));

      return isAccessible;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'check failed',
      }));
      return false;
    }
  }, [setState]);

  // 手动刷新
  const refresh = useCallback(() => checkGoogle(), [checkGoogle]);

  // 监听网络状态变化
  useEffect(() => {
    const cleanup = watchNetworkStatus((isOnline) => {
      setState((prev) => ({ ...prev, isOnline }));
      // 当网络恢复时，自动检测 Google 连接
      isOnline && autoCheck && checkGoogle();
    });

    return cleanup;
  }, [autoCheck, checkGoogle, setState]);

  // 初始检测
  useEffect(() => {
    autoCheck && checkGoogle();
  }, [autoCheck, checkGoogle]);

  // 定时检测
  useInterval(
    () => checkGoogle(),
    // state.isGoogleAccessible !== null 等检测完再检测，不然会累积多次检测
    state.isOnline && state.isGoogleAccessible !== null ? checkInterval : null
  );

  return {
    ...state,
    checkGoogle,
    refresh,
  };
};

export const useConnectivityState = () => useAtomValue(ConnectivityStateAtom);
